import { FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { RemediationItemSummary } from '@libs/types';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { ConfirmationModal } from '@components/ConfirmationModal';
import { PageHeader } from '@components/PageHeader';
import { RemediationItemStatus, RemediationRunStatus } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChecksIcon,
  SparkleIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import { useFinalizeRemediationRun, useRemediationRun } from '../api/remediation-api';
import { RemediationRunSkeleton } from '../components/RemediationSkeletons';
import { ItemStatusBadge } from '../components/RemediationStatusBadge';
import { useRunPlaceholders } from '../hooks/useRunPlaceholders';
import { unfilledPlaceholders } from '../utils/placeholders';
import { appliedCount } from '../utils/run-counts';

/**
 * What each outcome means for the assessment, which is the question this page
 * exists to answer — the item badge says what happened to a document, not what
 * it costs.
 */
const OUTCOME_ORDER: RemediationItemStatus[] = [
  RemediationItemStatus.ACCEPTED,
  RemediationItemStatus.REJECTED,
  RemediationItemStatus.NO_CHANGES,
  RemediationItemStatus.FAILED,
];

const DocumentRow: FC<{ item: RemediationItemSummary }> = observer(({ item }) => {
  const theme = useTheme();
  const { t } = useTrans();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        minWidth: 0,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="textSM_medium" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
          {item.documentName ?? t('app.remediation.summary.untitledDocument')}
        </Typography>
        {/* The reason a document failed belongs next to the document, not in a
            toast that has already gone — this is the page a user retries from. */}
        {item.status === RemediationItemStatus.FAILED && item.failureReason && (
          <Typography
            variant="textXS_regular"
            sx={{ display: 'block', color: 'error.main', mt: 0.25 }}
          >
            {item.failureReason}
          </Typography>
        )}
      </Box>
      {item.domainKey && (
        <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', flexShrink: 0 }}>
          {item.domainKey}
        </Typography>
      )}
      <Box sx={{ flexShrink: 0 }}>
        <ItemStatusBadge status={item.status} />
      </Box>
    </Box>
  );
});

/**
 * Where a reviewed run is turned into a new assessment version — or not yet.
 *
 * Review ends with every document decided and nothing showing what those
 * decisions add up to, so the irreversible step was being taken from a confirm
 * dialog: fork version N+1 and attach the accepted documents. That is a lot to
 * agree to from a modal that can only fit two sentences.
 *
 * This shows the outcome first — how many documents were accepted, what
 * happened to the rest, which ones failed and why — and then offers the two
 * moves that actually exist. Accepting is the end of the road for the run: it
 * folds the accepted documents into a new version and closes the run, and that
 * version is a draft the user submits themselves. If some of the assessment's
 * gaps have not been remediated yet, going back to the scope and running another
 * pass first is usually the better one, and there was previously nothing on this
 * path that said so.
 */
export const RemediationSummaryPage: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { runId } = useParams<{ runId: string }>();

  const { data: run, isLoading, isError } = useRemediationRun(runId);
  const finalize = useFinalizeRemediationRun();
  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * The blanks nobody filled, for the warning below.
   *
   * The fill step lets a blank be skipped — the user may not know the answer
   * today, and holding the run hostage to one unknown fact left people stuck.
   * The cost lands here: a skipped token appears verbatim in the rendered
   * document, and finalizing is the point of no return. So this screen counts
   * them and says so, without blocking — which is the same bargain the fill step
   * makes, restated at the last moment it can still be acted on.
   *
   * Only asked while the answer could still land. The banner says this is the
   * last point where a blank can be filled, which stops being true the moment
   * the run leaves review — and `useRunPlaceholders` pulls the full review
   * payload, the heaviest read in the feature, so a finalized run should not be
   * paying for it to be told something it cannot act on.
   */
  const alreadyFinalized =
    run?.status === RemediationRunStatus.FINALIZING ||
    run?.status === RemediationRunStatus.COMPLETED;
  const { placeholders } = useRunPlaceholders(runId, !!run && !alreadyFinalized);
  // Split from `placeholders` deliberately: this page polls the run, and folding
  // both into one memo re-collected every token on each tick to re-read a map
  // that had not changed.
  const unfilledBlanks = useMemo(
    () => unfilledPlaceholders(placeholders, run?.placeholderValues ?? {}),
    [placeholders, run?.placeholderValues],
  );

  const byOutcome = useMemo(() => {
    const groups = new Map<RemediationItemStatus, RemediationItemSummary[]>();
    (run?.items ?? []).forEach((item) => {
      const bucket = groups.get(item.status);
      if (bucket) bucket.push(item);
      else groups.set(item.status, [item]);
    });
    return groups;
  }, [run]);

  if (isLoading) return <RemediationRunSkeleton />;

  if (isError || !run) {
    return (
      <Box sx={{ p: 3 }}>
        <CalloutBanner tone="error" title={t('app.remediation.summary.loadError')} />
      </Box>
    );
  }

  const accepted = appliedCount(run.itemCounts);
  const failed = run.itemCounts.failed;
  // Anything the AI never settled. Finalizing with these outstanding would fork a
  // version that is missing documents the user is still expecting.
  const outstanding = run.itemCounts.inFlight + run.itemCounts.reviewable;

  const onFinalize = () => {
    if (!runId) return;
    setConfirmOpen(false);
    finalize.mutate(runId, {
      onSuccess: () => {
        enqueueSnackbar(t('app.remediation.toast.finalizeStarted'), { variant: 'success' });
        navigate(routes.remediationRun(runId));
      },
    });
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', backgroundColor: 'background.paper', borderRadius: 4 }}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PageHeader
          title={t('app.remediation.summary.title')}
          description={t('app.remediation.summary.subtitle')}
        />

        {/* The three numbers the decision below turns on. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 2,
          }}
        >
          {(
            [
              ['accepted', accepted, theme.palette.accent.green],
              ['failed', failed, theme.palette.error.main],
              ['outstanding', outstanding, theme.palette.warning.main],
            ] as const
          ).map(([key, value, colour], i) => (
            <Box
              key={key}
              sx={{
                p: 2,
                borderInlineStart: i === 0 ? 'none' : `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <Typography
                variant="textXS_regular"
                sx={{ color: 'text.secondary', display: 'block' }}
              >
                {t(`app.remediation.summary.stat.${key}` as SingularKeys)}
              </Typography>
              <Typography
                variant="displayXS_bold"
                sx={{ display: 'block', color: value > 0 ? colour : 'text.tertiary' }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Said before the buttons, not after a failed click: the version takes
            the documents as they stand, so a run with documents still
            outstanding is not ready to be folded into one. */}
        {outstanding > 0 && (
          <CalloutBanner
            tone="warning"
            title={t('app.remediation.summary.outstandingTitle')}
          >
            {t('app.remediation.summary.outstandingBody')}
          </CalloutBanner>
        )}
        {failed > 0 && outstanding === 0 && (
          <CalloutBanner tone="warning" title={t('app.remediation.summary.failedTitle')}>
            {t('app.remediation.summary.failedBody')}
          </CalloutBanner>
        )}

        {/* Advisory, not a gate — the finalize button below stays enabled. The
            fill step is still open until the run is applied, so this is the last
            moment the answer can be supplied, and the only one where the user is
            looking at what applying actually produces. */}
        {unfilledBlanks.length > 0 && (
          <CalloutBanner
            tone="warning"
            title={t('app.remediation.summary.blanksTitle')}
            action={
              <Button
                variant="secondary"
                onClick={() => navigate(routes.remediationPlaceholders(runId ?? ''))}
              >
                {t('app.remediation.summary.blanksCta')}
              </Button>
            }
          >
            {t('app.remediation.summary.blanksBody', { count: unfilledBlanks.length })}
          </CalloutBanner>
        )}

        {/* Grouped by outcome rather than listed flat: "what did this run produce"
            and "what do I still have to deal with" are different questions, and a
            flat list answers neither without reading every row. */}
        {OUTCOME_ORDER.filter((status) => (byOutcome.get(status)?.length ?? 0) > 0).map(
          (status) => {
            const items = byOutcome.get(status) ?? [];
            return (
              <Paper
                key={status}
                elevation={0}
                sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 2 }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: '8px 8px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="textSM_semiBold">
                    {t(`app.remediation.summary.group.${status}` as SingularKeys)}
                  </Typography>
                  <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                    {items.length}
                  </Typography>
                </Box>
                {items.map((item) => (
                  <DocumentRow key={item.itemId} item={item} />
                ))}
              </Paper>
            );
          },
        )}

        {/* Both moves, side by side, because neither is the obvious one: it
            depends on whether the assessment still has gaps worth another pass. */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.grey[300]}`,
            pt: 2,
          }}
        >
          <Button
            variant="secondary"
            startIcon={<ArrowLeftIcon size={16} />}
            onClick={() => navigate(routes.remediationReview(runId ?? ''))}
          >
            {t('app.remediation.summary.backToReview')}
          </Button>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              startIcon={<SparkleIcon size={16} weight="fill" />}
              onClick={() => navigate(routes.remediationScope(run.assessmentId))}
            >
              {t('app.remediation.summary.keepRemediating')}
            </Button>
            <Button
              variant="primary"
              startIcon={<ChecksIcon size={16} />}
              disabled={outstanding > 0 || accepted === 0 || alreadyFinalized || finalize.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              {t('app.remediation.summary.acceptAndVersion')}
            </Button>
          </Box>
        </Box>
      </Box>

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        icon={<WarningIcon size={20} />}
        iconColor="warning"
        title={t('app.remediation.summary.confirmTitle')}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              {t('app.remediation.summary.confirmBody', { count: accepted })}
            </Typography>
            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              {t('app.remediation.summary.confirmDraft')}
            </Typography>
          </Box>
        }
        buttons={[
          {
            label: t('app.common.cancel'),
            variant: 'outlined',
            onClick: () => setConfirmOpen(false),
          },
          {
            label: t('app.remediation.summary.confirmCta'),
            variant: 'contained',
            color: 'primary',
            startIcon: <ArrowRightIcon size={16} />,
            onClick: onFinalize,
          },
        ]}
      />
    </Box>
  );
});
